import { type TransferListItem, type TransferListQuery } from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  UzConfirmDialog,
  UzDropdownMenu,
  UzInput,
  UzStatusBadge,
} from '@utazzo/ui';
import { motion } from 'framer-motion';
import {
  Bus,
  Car,
  Copy,
  FileSpreadsheet,
  FilterX,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Ship,
  TrainFront,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TransferCard } from '../components/TransferCard';
import { listTransfers } from '../transfers.api';
import { useTransferGeoFilters } from '../use-transfer-geo-filters';
import {
  useBulkDeleteTransfers,
  useDeleteTransfer,
  useDuplicateTransfer,
  useTransfersList,
} from '../use-transfers';

import {
  DataGrid,
  DataGridFilterChip,
  DataGridPagination,
  type DataGridColumn,
  type DataGridFilter,
} from '@/components/data-grid';
import { useListScreen } from '@/components/list-screen';
import { downloadExcel, fetchAllPages } from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';
import { ReadinessCell } from '@/components/ReadinessCell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { withGeoFilters } from '@/hooks/use-catalog-geo-filters';
import { stagger } from '@/lib/motion';

/**
 * Transfers master list. Same two-view identity as Hotels / Activities:
 *   - Grid (default) - TransferCard grid with hero + route + INR price.
 *   - List - DataGrid with sortable columns for ops + bulk actions.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const TRANSFER_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'ONE_WAY', label: 'One-way' },
  { value: 'ROUND_TRIP', label: 'Round trip' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'DAY_HIRE', label: 'Day hire' },
  { value: 'MULTI_STOP', label: 'Multi-stop' },
] as const;

const TRANSFER_MODE_OPTIONS = [
  { value: '', label: 'All modes' },
  { value: 'CAR', label: 'Car' },
  { value: 'BUS', label: 'Bus' },
  { value: 'TRAIN', label: 'Train' },
  { value: 'FERRY', label: 'Ferry' },
] as const;

const TRANSFER_MODE_META: Record<
  TransferListItem['transferMode'],
  { label: string; Icon: typeof Car }
> = {
  CAR: { label: 'Car', Icon: Car },
  BUS: { label: 'Bus', Icon: Bus },
  TRAIN: { label: 'Train', Icon: TrainFront },
  FERRY: { label: 'Ferry', Icon: Ship },
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'All sub-categories' },
  { value: 'AIRPORT', label: 'Airport' },
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'INTERCITY', label: 'Intercity' },
  { value: 'OTHER', label: 'Other' },
] as const;

const CATEGORY_LABEL: Record<string, string> = {
  AIRPORT: 'Airport',
  HOTEL: 'Hotel',
  INTERCITY: 'Intercity',
  OTHER: 'Other',
};

const APPROVAL_OPTIONS = [
  { value: '', label: 'All approvals' },
  { value: 'approved', label: 'Approved only' },
  { value: 'pending', label: 'Not approved' },
] as const;

const ARCHIVED_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'archived', label: 'Include archived' },
] as const;

/**
 * The Readiness column, turned into two filters so "what still needs work?" is
 * one click instead of a page-by-page read of the chips. Both are server-side,
 * so the count and the pagination describe the filtered set, not the page.
 */
const IMAGES_OPTIONS = [
  { value: '', label: 'Any images' },
  { value: 'with', label: 'With images' },
  { value: 'without', label: 'Without images' },
] as const;

const INFO_OPTIONS = [
  { value: '', label: 'Any information' },
  { value: 'missing', label: 'Missing information' },
  { value: 'complete', label: 'Complete information' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'transferMode',
    label: 'Mode',
    options: TRANSFER_MODE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'transferType',
    label: 'Type',
    options: TRANSFER_TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'transferCategory',
    label: 'Sub-category',
    options: CATEGORY_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'approval',
    label: 'Approval',
    options: APPROVAL_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'images',
    label: 'Images',
    options: IMAGES_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'info',
    label: 'Info',
    options: INFO_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
  {
    id: 'archived',
    label: 'Archived',
    options: ARCHIVED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

const TRANSFER_TYPE_LABEL: Record<TransferListItem['transferType'], string> = {
  ONE_WAY: 'One-way',
  ROUND_TRIP: 'Round trip',
  HOURLY: 'Hourly',
  DAY_HIRE: 'Day hire',
  MULTI_STOP: 'Multi-stop',
};

const formatInr = (amount: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const mapStatus = (s: TransferListItem['status']): 'active' | 'inactive' | 'draft' | 'pending' => {
  switch (s) {
    case 'ACTIVE':
      return 'active';
    case 'DRAFT':
      return 'draft';
    case 'PENDING_APPROVAL':
      return 'pending';
    default:
      return 'inactive';
  }
};

// ---------------------------------------------------------------------------
// View toggle
// ---------------------------------------------------------------------------

export type View = 'grid' | 'list';

export const ViewToggle = ({
  view,
  onChange,
}: {
  view: View;
  onChange: (next: View) => void;
}): React.JSX.Element => {
  const btn = (target: View, Icon: typeof LayoutGrid, label: string): React.JSX.Element => (
    <button
      type="button"
      role="tab"
      aria-selected={view === target}
      onClick={() => onChange(target)}
      className={
        'relative inline-flex h-7 items-center gap-1.5 rounded-[5px] px-2.5 text-xs font-medium transition-colors duration-150 ' +
        (view === target
          ? 'bg-white text-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-zinc-200'
          : 'text-zinc-500 hover:text-zinc-800')
      }
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );
  return (
    <div role="tablist" aria-label="View mode" className="inline-flex rounded-md bg-zinc-100 p-0.5">
      {btn('grid', LayoutGrid, 'Grid')}
      {btn('list', ListIcon, 'List')}
    </div>
  );
};

const GridToolbar = ({
  filters,
  search,
  onSearchChange,
  filterState,
  onFilterChange,
  resultCount,
}: {
  filters: ReadonlyArray<DataGridFilter>;
  search: string;
  onSearchChange: (q: string) => void;
  filterState: Record<string, string>;
  onFilterChange: (id: string, value: string) => void;
  resultCount: number;
}): React.JSX.Element => {
  const activeFilters = filters.filter((f) => Boolean(filterState[f.id]));
  return (
    <div className="mb-4 flex flex-col gap-2">
      {/* Two bands, mirroring DataGridToolbar: the list's own controls up top,
          the filter chips on their own full-width row underneath so a long
          chip set reads as one line. */}
      <div className="flex flex-wrap items-center gap-2">
        <UzInput
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search transfers..."
          leftAdornment={<Search className="h-3.5 w-3.5" aria-hidden />}
          containerClassName="w-64"
        />
        <span className="text-[13px] whitespace-nowrap text-zinc-500 tabular-nums">
          Showing <span className="font-medium text-zinc-700">{resultCount}</span>
          {' results'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => (
          <DataGridFilterChip
            key={f.id}
            filter={f}
            value={filterState[f.id] ?? ''}
            onChange={(v) => onFilterChange(f.id, v)}
          />
        ))}
        {activeFilters.length > 0 && (
          <button
            type="button"
            title="Clear filters"
            onClick={() => filters.forEach((f) => onFilterChange(f.id, ''))}
            className="focus-visible:ring-ring inline-flex h-[34px] cursor-pointer items-center gap-1 rounded-md px-1.5 text-[13px] text-[var(--fg-muted)] transition-colors hover:text-[var(--fg)] focus-visible:ring-2 focus-visible:outline-none"
          >
            <FilterX className="h-3.5 w-3.5" aria-hidden />
            Clear · {activeFilters.length}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface TransfersListPageProps {
  embedded?: boolean;
  /**
   * Controlled view mode. When provided (Inbox host owns the ViewToggle in
   * its own action bar), the local state is bypassed. Uncontrolled otherwise.
   */
  view?: View;
  onViewChange?: (next: View) => void;
}

export const TransfersListPage = ({
  embedded,
  view: viewProp,
  onViewChange,
}: TransfersListPageProps = {}): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteTransfer = useDeleteTransfer();
  const bulkDeleteMut = useBulkDeleteTransfers();
  const duplicateTransfer = useDuplicateTransfer();
  const navigate = useNavigate();
  // Default to `list` so the DataGrid (with search, filters, bulk actions) is
  // the first thing operators see; grid is a per-card visual browse mode.
  const [localView, setLocalView] = useState<View>('list');
  const view = viewProp ?? localView;
  const setView = onViewChange ?? setLocalView;

  // City options narrow to the selected Country (read from the URL inside the
  // hook, so the filters are ready before the grid state is built).
  const { countryFilter, cityFilter } = useTransferGeoFilters();
  const filters = useMemo<ReadonlyArray<DataGridFilter>>(
    () => withGeoFilters(FILTERS, countryFilter, cityFilter, 'transferCategory'),
    [countryFilter, cityFilter],
  );

  const screen = useListScreen<TransferListItem, TransferListQuery>({
    title: 'Transfers',
    useList: useTransfersList,
    defaultSort: { by: 'createdAt', dir: 'desc' },
    minSearchLength: MIN_SEARCH_LENGTH,
    filters,
    getRowId: (row) => row.id,
    columns: [],
    toQuery: (state, base) => {
      const status = state.filters.status;
      const transferMode = state.filters.transferMode;
      const transferType = state.filters.transferType;
      const transferCategory = state.filters.transferCategory;
      const countryId = state.filters.countryId;
      const cityId = state.filters.cityId;
      const approval = state.filters.approval;
      const archived = state.filters.archived;
      const images = state.filters.images;
      const info = state.filters.info;
      return {
        ...base,
        ...(status ? { status: status as TransferListQuery['status'] } : {}),
        ...(transferMode
          ? { transferMode: transferMode as TransferListQuery['transferMode'] }
          : {}),
        ...(transferType
          ? { transferType: transferType as TransferListQuery['transferType'] }
          : {}),
        ...(transferCategory
          ? { transferCategory: transferCategory as TransferListQuery['transferCategory'] }
          : {}),
        ...(countryId ? { countryId } : {}),
        ...(cityId ? { cityId } : {}),
        ...(approval === 'approved' ? { isApprovedByAdmin: true } : {}),
        ...(approval === 'pending' ? { isApprovedByAdmin: false } : {}),
        ...(images === 'with' ? { hasImages: true } : {}),
        ...(images === 'without' ? { hasImages: false } : {}),
        ...(info === 'missing' ? { missingInfo: true } : {}),
        ...(info === 'complete' ? { missingInfo: false } : {}),
        ...(archived === 'archived' ? { includeDeleted: true } : {}),
      };
    },
  });

  const { state, gridState, query, list, data, pagination, deletion } = screen;

  const listColumns: ReadonlyArray<DataGridColumn<TransferListItem>> = [
    {
      id: 'serial',
      header: 'S.No',
      width: '64px',
      cell: (_row, index) => (
        <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
      ),
    },
    // No cover-thumbnail column: transfers rarely carry a photo, so it read as
    // a column of empty placeholders. The Readiness chip already reports the
    // image count, and the Grid view is where pictures actually matter.
    {
      id: 'name',
      header: 'Transfer',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-zinc-900">{row.name}</span>
          {row.legacyCode && <span className="text-xs text-zinc-500">{row.legacyCode}</span>}
        </div>
      ),
    },
    {
      id: 'route',
      header: 'Route',
      cell: (row) => (
        <div className="flex flex-col text-sm">
          <span className="text-zinc-900">
            {row.fromLocationName ?? <span className="text-zinc-400">unset</span>}
            <span className="mx-1 text-zinc-400">-&gt;</span>
            {row.toLocationName ?? <span className="text-zinc-400">unset</span>}
          </span>
          <span className="text-xs text-zinc-500">
            {row.cityName}, {row.countryName}
          </span>
        </div>
      ),
    },
    {
      id: 'mode',
      header: 'Mode',
      width: '120px',
      cell: (row) => {
        const { label, Icon } = TRANSFER_MODE_META[row.transferMode];
        return (
          <div className="flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-1.5 text-sm whitespace-nowrap text-zinc-900">
              <Icon className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
              {label}
            </span>
            {row.operatorName && (
              <span className="max-w-35 truncate text-xs text-zinc-500">{row.operatorName}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'rates',
      header: 'Rates',
      cell: (row) => (
        <div className="flex flex-col text-sm">
          <span className="whitespace-nowrap text-zinc-900">
            {row.rateCount > 0 ? (
              `${row.rateCount} rate${row.rateCount === 1 ? '' : 's'}`
            ) : (
              <span className="text-zinc-400">-</span>
            )}
          </span>
          {row.baseRateServiceType && (
            <span className="text-xs whitespace-nowrap text-zinc-500">
              {row.baseRateServiceType === 'SIC' ? 'shared / SIC' : 'private'}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'transferType',
      header: 'Type',
      sortable: true,
      width: '140px',
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <UzBadge tone="secondary">{TRANSFER_TYPE_LABEL[row.transferType]}</UzBadge>
          {row.transferCategory && (
            <span className="text-xs text-zinc-500">{CATEGORY_LABEL[row.transferCategory]}</span>
          )}
        </div>
      ),
    },
    {
      id: 'vendor',
      header: 'Vendor',
      cell: (row) => (
        <span className="text-sm text-zinc-900">
          {row.vendorSupplierName ?? <span className="text-zinc-400">-</span>}
        </span>
      ),
    },
    {
      id: 'cost',
      header: 'From',
      width: '170px',
      cell: (row) => (
        <div className="flex flex-col items-end text-right">
          {row.baseRatePriceInr > 0 ? (
            <>
              <span className="text-base font-semibold text-zinc-900 tabular-nums">
                {formatInr(row.baseRatePriceInr)}
              </span>
              {row.currencyCode && row.currencyCode !== 'INR' && (
                <span className="text-[10px] text-zinc-500 tabular-nums">
                  {row.currencyCode} {row.baseRatePrice.toLocaleString('en-IN')}
                </span>
              )}
            </>
          ) : (
            <span className="text-base font-semibold text-zinc-400 tabular-nums">-</span>
          )}
          {row.baseRateServiceType === 'SIC' && (
            <span className="text-[10px] text-zinc-500">per person</span>
          )}
          {row.baseRateServiceType === 'PRIVATE' && (
            <span className="text-[10px] text-zinc-500">per vehicle</span>
          )}
        </div>
      ),
    },
    {
      id: 'readiness',
      header: 'Readiness',
      width: '130px',
      cell: (row) => (
        <ReadinessCell
          photoCount={row.photoCount + row.galleryCount}
          // Vendor is deliberately NOT a readiness check - plenty of transfers
          // are run without a supplier row and are perfectly sellable.
          checks={[
            { label: 'Rates', ok: row.rateCount > 0 },
            { label: 'Price', ok: row.baseRatePrice > 0 },
          ]}
        />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '140px',
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <UzStatusBadge status={mapStatus(row.status)} />
          {row.isApprovedByAdmin && (
            <span className="text-[10px] tracking-wide text-emerald-600 uppercase">
              Admin approved
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '52px',
      align: 'center',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => (
        <div
          className="flex justify-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <UzDropdownMenu
            align="end"
            trigger={
              <UzButton variant="ghost" size="icon" aria-label="Row actions">
                <MoreHorizontal className="h-4 w-4" />
              </UzButton>
            }
            items={[
              {
                label: 'Duplicate to draft',
                icon: <Copy className="h-4 w-4" />,
                disabled: duplicateTransfer.isPending,
                onSelect: () => void duplicateTransfer.mutateAsync(row.id),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const onOpenTransfer = (row: TransferListItem): void => {
    navigate(`/transfers/${row.id}/edit`);
  };

  const content = (
    <>
      {view === 'list' ? (
        <DataGrid<TransferListItem>
          columns={listColumns}
          data={data}
          pagination={pagination}
          // The transfers grid is column-rich (cover, route, mode, rates, type,
          // vendor, price, readiness, status). Auto layout sizes columns to
          // content and lets the wrapper scroll horizontally instead of the
          // fixed layout collapsing narrow cells (which wrapped text vertically).
          tableLayout="auto"
          isLoading={list.isLoading}
          isError={list.isError}
          error={{
            title: 'Failed to load transfers',
            description: 'The server returned an error. Try refreshing or check your network.',
          }}
          onRetry={() => void list.refetch()}
          state={state}
          onStateChange={{
            setSearch: gridState.setSearch,
            setFilter: gridState.setFilter,
            setSort: gridState.setSort,
            setPage: gridState.setPage,
            setPageSize: gridState.setPageSize,
            setDensity: gridState.setDensity,
            setHidden: gridState.setHidden,
          }}
          filters={filters}
          getRowId={(row) => row.id}
          onRowClick={onOpenTransfer}
          searchPlaceholder="Search by name, code, city, route or vendor (min 3 characters)"
          emptyTitle="No transfers match these filters"
          emptyDescription="Adjust filters or clear the search box."
          selectable
          bulkActions={[
            {
              id: 'bulk-delete',
              label: 'Delete',
              danger: true,
              onAction: (_ids, rows) => {
                void bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
              },
            },
          ]}
          onExport={async () => {
            const rows = await fetchAllPages(listTransfers, query);
            downloadExcel(
              rows,
              [
                { header: 'Name', value: (r) => r.name },
                { header: 'Code', value: (r) => r.legacyCode ?? '' },
                {
                  header: 'Route',
                  value: (r) => `${r.fromLocationName ?? ''} -> ${r.toLocationName ?? ''}`,
                },
                { header: 'City', value: (r) => r.cityName },
                { header: 'Country', value: (r) => r.countryName },
                { header: 'Mode', value: (r) => TRANSFER_MODE_META[r.transferMode].label },
                { header: 'Operator', value: (r) => r.operatorName ?? '' },
                { header: 'Rates', value: (r) => r.rateCount },
                { header: 'Transfer type', value: (r) => TRANSFER_TYPE_LABEL[r.transferType] },
                {
                  header: 'Sub-category',
                  value: (r) => (r.transferCategory ? CATEGORY_LABEL[r.transferCategory] : ''),
                },
                { header: 'Vendor', value: (r) => r.vendorSupplierName ?? '' },
                { header: 'Base rate (native)', value: (r) => r.baseRatePrice },
                { header: 'Base rate (INR)', value: (r) => r.baseRatePriceInr },
                { header: 'Currency', value: (r) => r.currencyCode ?? '' },
                { header: 'Status', value: (r) => r.status },
              ],
              'transfers',
            );
          }}
        />
      ) : (
        <>
          <GridToolbar
            filters={filters}
            search={state.q}
            onSearchChange={gridState.setSearch}
            filterState={state.filters}
            onFilterChange={gridState.setFilter}
            resultCount={pagination?.totalItems ?? 0}
          />
          {list.isLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[228px] animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60"
                />
              ))}
            </div>
          ) : list.isError ? (
            <div className="rounded-lg border border-zinc-200 bg-white p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900">Failed to load transfers</h3>
              <p className="mx-auto mt-1 max-w-sm text-xs text-zinc-500">
                The server returned an error. Check your connection and try again.
              </p>
              <UzButton
                size="sm"
                variant="outline"
                onClick={() => void list.refetch()}
                className="mt-4"
              >
                Retry
              </UzButton>
            </div>
          ) : data.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900">No transfers match</h3>
              <p className="mx-auto mt-1 max-w-sm text-xs text-zinc-500">
                Adjust filters or clear the search box.
              </p>
            </div>
          ) : (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {data.map((transfer) => (
                <TransferCard key={transfer.id} transfer={transfer} onClick={onOpenTransfer} />
              ))}
            </motion.div>
          )}
          {pagination && data.length > 0 && (
            <div className="mt-4 rounded-md border border-zinc-200 bg-white">
              <DataGridPagination
                page={pagination.page}
                pageSize={pagination.pageSize}
                totalItems={pagination.totalItems}
                onPageChange={gridState.setPage}
                onPageSizeChange={gridState.setPageSize}
              />
            </div>
          )}
        </>
      )}

      {canDelete && (
        <UzConfirmDialog
          open={deletion.target !== null}
          onOpenChange={(open) => {
            if (!open) deletion.clear();
          }}
          title="Delete this transfer?"
          description={
            deletion.target
              ? `${deletion.target.name} will be soft-deleted and hidden from lists. Itinerary components using it are unaffected.`
              : ''
          }
          confirmLabel="Delete"
          tone="destructive"
          onConfirm={async () => {
            if (deletion.target) {
              await deleteTransfer.mutateAsync(deletion.target.id);
              deletion.clear();
            }
          }}
        />
      )}
    </>
  );

  if (embedded) return content;

  return (
    <PageShell
      title="Transfers"
      description="Point-to-point transport. Per-vehicle pricing, capacity-first."
      actions={
        <>
          <ViewToggle view={view} onChange={setView} />
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => void list.refetch()}
            loading={list.isFetching}
            aria-label="Refresh"
          >
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Refresh
          </UzButton>
          <UzButton variant="outline" size="sm" onClick={() => navigate('/transfers/imports')}>
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />
            Bulk import
          </UzButton>
          <UzButton onClick={() => navigate('/transfers/new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add transfer
          </UzButton>
        </>
      }
    >
      {content}
    </PageShell>
  );
};
